import React, { useEffect, useMemo, useState } from "react";
import { DataSourcesStore } from "./stores/DataSourcesStore";
import { IntegrationStore } from "./stores/IntegrationsStore";
import { DataHandlerStore } from "./stores/DataHandlerStore";

import { parseFromISO, standardDateFormat } from "utils/DateTimeUtils";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import AcxButton from "components/UI/AcxButton";
import { OrganizationMetadataFileTypeExtension } from "models/OrganizationModels/OrganizationMetadataFileTypeExtension";
import { Box, IconButton, Typography } from "@mui/material";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
    metadataSourceOptions,
    OrganizationMetadataFileType,
} from "./helpers/constants";
import AddButton from "./components/AddButton";
import DeleteIcon from "@mui/icons-material/Delete";
import theme from "Theme/AppTheme";
import { observer } from "mobx-react";
import { DataSourceType, IntegrationType } from "./types";

type DataSourcesTableProps = {
    dataSourcesStore: DataSourcesStore;
    integrationsStore: IntegrationStore;
    dataHandlerStore: DataHandlerStore;
};

const DataSourcesTable: React.FC<DataSourcesTableProps> = observer(
    ({ dataHandlerStore, dataSourcesStore, integrationsStore }) => {
        const { dataGridStore, setActiveStep, setSelectedType, canEdit } =
            dataHandlerStore;
        const {
            setSelectedDataSource,
            createDataSource,
            dataSources,
            deleteDataSources,
            isLoading: isDataSourcesLoading,
        } = dataSourcesStore;
        const {
            setSelectedIntegration,
            deleteIntegrations,
            shownIntegrations,
            createIntegration,
            isLoading: isIntegrationsLoading,
        } = integrationsStore;
        const [showAddDataSourceModal, setShowAddDataSourceModal] =
            useState(false);
        const [showDeleteConfirmation, setShowDeleteConfirmation] =
            useState(false);
        const [searchValue, setSearchValue] = useState("");
        const [selectedSource, setSelectedSource] = useState("");

        dataGridStore.isLoading = isDataSourcesLoading || isIntegrationsLoading;

        // Define columns with responsive widths
        const columns: IColDef[] = useMemo(
            () => [
                {
                    headerName: "Configuration Name",
                    field: "name",
                    flex: 2,
                    resizable: false,
                    valueGetter: (_, row) => row.name || row.type,
                    renderCell: (params) => (
                        <AcxButton
                            variant="text"
                            containerStyle={{ height: "100%" }}
                            sx={{ height: "100%", mx: -1, fontSize: 12 }} // Adjusted to line up with the column header
                            onClick={() => {
                                if (params.row.type) {
                                    setSelectedType("Integration");
                                    setSelectedIntegration(params.row);
                                } else {
                                    setSelectedType("DataSource");
                                    setSelectedDataSource(params.row);
                                }
                                setActiveStep(1);
                            }}
                        >
                            {params.row.name || params.row.type}
                        </AcxButton>
                    ),
                },
                {
                    headerName: "Source",
                    field: "metaDataFileType",
                    flex: 1,
                    resizable: false,
                    valueGetter: (_, row) =>
                        OrganizationMetadataFileType[row.metaDataFileType] ||
                        row.type,
                },
                {
                    headerName: "Extension",
                    field: "metaDataFileTypeExtension",
                    flex: 1,
                    resizable: false,
                    valueGetter: (_, row) => {
                        const fileType =
                            OrganizationMetadataFileType[row.metaDataFileType];

                        if (fileType === "JSON" && row.chatFileSchemaHandler) {
                            return "JSON_CHAT";
                        }

                        if (fileType === "FILENAME") {
                            return "";
                        }

                        return OrganizationMetadataFileTypeExtension[
                            row.metaDataFileTypeExtension
                        ];
                    },
                },
                {
                    headerName: "Last Updated On",
                    field: "modifiedOn",
                    flex: 1,
                    resizable: false,
                    valueGetter: (_, row) =>
                        standardDateFormat(
                            parseFromISO(
                                row.modifiedOn || row.lastUpdate || undefined,
                                true,
                            ),
                            "MM/DD/YYYY",
                        ),
                },
            ],
            [
                setActiveStep,
                setSelectedDataSource,
                setSelectedIntegration,
                setSelectedType,
            ],
        );

        useEffect(() => {
            dataGridStore.setColumns(columns);
        }, [columns, dataGridStore]);

        useEffect(() => {
            const sourcesAndIntegrations = [
                ...dataSources,
                ...shownIntegrations,
            ];
            if (!searchValue) {
                dataGridStore.rows = sourcesAndIntegrations;
                return;
            }

            dataGridStore.rows = sourcesAndIntegrations.filter((source) => {
                const compareValue =
                    (source as DataSourceType).name ||
                    (source as IntegrationType).type;
                return compareValue
                    ?.toLowerCase()
                    ?.includes(searchValue.toLowerCase().trim());
            });
        }, [searchValue, dataGridStore, dataSources, shownIntegrations]);

        return (
            <Box sx={{ height: "80vh" }}>
                <Box sx={{ display: "flex", width: "100%" }}>
                    <AcxMainTextField
                        key="datasources-search"
                        id="datasources-search"
                        value={searchValue}
                        placeholderText="Search data sources..."
                        onChange={(e) => setSearchValue(e.target.value)}
                        containerStyle={{
                            minWidth: "225px",
                            width: "20%",
                            marginRight: "auto",
                        }}
                        startAdornment={
                            <SearchOutlinedIcon
                                htmlColor={theme.palette.gray[400]}
                                fontSize="small"
                                sx={{ marginRight: 1 }}
                            />
                        }
                    />

                    {canEdit && (
                        <IconButton
                            disabled={!dataGridStore.selectedRows.length}
                            sx={{
                                background: "transparent",
                                marginRight: 1,
                                padding: theme.spacing(0, 1),
                            }}
                            onClick={() => setShowDeleteConfirmation(true)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}

                    {canEdit && (
                        <AddButton
                            color="white"
                            onClick={() => setShowAddDataSourceModal(true)}
                            sx={{
                                color: theme.palette.blackFont.main,
                                height: "100%",
                            }}
                            disabled={dataGridStore.isLoading}
                        >
                            Data Source
                        </AddButton>
                    )}
                </Box>
                <AcxDataGrid
                    dataGridStore={dataGridStore}
                    disableColumnResize
                />
                {/* Add Data Source Modal */}
                <AcxDialog
                    title={"New Data Source Configuration"}
                    text={"Configure New Metadata Source"}
                    isOpen={showAddDataSourceModal}
                    onClose={() => setShowAddDataSourceModal(false)}
                    contentWidth="400px"
                    dialogContentChildren={
                        <AcxSelect
                            inputLabel="Select Metadata Source"
                            id={"select-metadata-source"}
                            options={metadataSourceOptions}
                            onChange={(e) => setSelectedSource(e.value)}
                        />
                    }
                >
                    <AcxButton
                        color="primary"
                        disabled={!selectedSource}
                        onClick={() => {
                            if (selectedSource === "AmazonConnect") {
                                setSelectedType("Integration");
                                createIntegration(selectedSource);
                            } else {
                                setSelectedType("DataSource");
                                createDataSource(selectedSource);
                            }
                            setActiveStep(1);
                            setShowAddDataSourceModal(false);
                        }}
                    >
                        Continue
                    </AcxButton>
                </AcxDialog>
                {/* Delete Confirmation Modal */}
                <AcxDialog
                    title={"Delete Data Source"}
                    text={`Are you sure you want to delete the selected data source${
                        dataGridStore.selectedRows.length > 1 ? "s" : ""
                    }?`}
                    isOpen={showDeleteConfirmation}
                    onClose={() => setShowDeleteConfirmation(false)}
                    contentWidth="400px"
                    dialogContentChildren={
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                            }}
                        >
                            {dataGridStore.selectedRows.map((row) => (
                                <Typography
                                    sx={{
                                        color: theme.palette.gray[500],
                                        fontWeight: 600,
                                    }}
                                    key={row.id}
                                >
                                    - {row.name || row.type}
                                </Typography>
                            ))}
                        </Box>
                    }
                >
                    <AcxButton
                        color="primary"
                        loading={dataGridStore.isLoading}
                        disabled={!canEdit}
                        onClick={async () => {
                            await deleteDataSources();
                            await deleteIntegrations();
                            setShowDeleteConfirmation(false);
                        }}
                    >
                        Delete
                    </AcxButton>
                </AcxDialog>
            </Box>
        );
    },
);

export default DataSourcesTable;
